import React, { useEffect, useMemo, useState } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import SortableTable from '../SortableTable';
import StatTile from '../ui/StatTile';
import StatisticsClientsOverview from './StatisticsClientsOverview';

const formatYMD = (d) => new Date(d).toISOString().split('T')[0];

const StatisticsClientsTab = ({ startDate, endDate, clientFilter, toolFilter, fetchWithAuth, apiBaseUrl, includeInternal }) => {
  const [metric, setMetric] = useState('visits'); // 'visits' | 'events'
  const [daily, setDaily] = useState([]);
  const [loadingDaily, setLoadingDaily] = useState(false);
  const [usersByTool, setUsersByTool] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Load daily usage for selected client
  useEffect(() => {
    const loadDaily = async () => {
      if (!clientFilter?.value) { setDaily([]); return; }
      setLoadingDaily(true);
      try {
        const params = new URLSearchParams({ startDate: formatYMD(startDate), endDate: formatYMD(endDate), client: clientFilter.value });
        if (toolFilter?.value) params.set('tool', toolFilter.value);
        const resp = await fetchWithAuth(`${apiBaseUrl}/api/dwh/bi/tools/stats/client-daily?${params.toString()}`);
        if (!resp.ok) { setDaily([]); return; }
        const json = await resp.json();
        setDaily(json.data || []);
      } finally {
        setLoadingDaily(false);
      }
    };
    loadDaily();
  }, [startDate, endDate, clientFilter, toolFilter, fetchWithAuth, apiBaseUrl]);

  // Load users-by-tool for selected client
  useEffect(() => {
    const loadUsersByTool = async () => {
      if (!clientFilter?.value) { setUsersByTool([]); return; }
      setLoadingUsers(true);
      try {
        const params = new URLSearchParams({ startDate: formatYMD(startDate), endDate: formatYMD(endDate), client: clientFilter.value });
        if (toolFilter?.value) params.set('tool', toolFilter.value);
        const resp = await fetchWithAuth(`${apiBaseUrl}/api/dwh/bi/tools/stats/client-users-by-tool?${params.toString()}`);
        if (!resp.ok) { setUsersByTool([]); return; }
        const json = await resp.json();
        setUsersByTool(json.data || []);
      } finally {
        setLoadingUsers(false);
      }
    };
    loadUsersByTool();
  }, [startDate, endDate, clientFilter, toolFilter, fetchWithAuth, apiBaseUrl]);

  // Build full range of days
  const fullRangeDays = useMemo(() => {
    const days = [];
    if (!startDate || !endDate) return days;
    const cur = new Date(startDate);
    cur.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(0, 0, 0, 0);
    while (cur.getTime() <= end.getTime()) {
      days.push(formatYMD(cur));
      cur.setDate(cur.getDate() + 1);
    }
    return days;
  }, [startDate, endDate]);

  // Chart data (sum across tools, optional filter already applied by API when provided)
  const chartData = useMemo(() => {
    const map = new Map(fullRangeDays.map(d => [d, { day: d, visits: 0, events: 0 }]));
    daily.forEach(d => {
      const key = formatYMD(d.DAY || d.day);
      const acc = map.get(key) || { day: key, visits: 0, events: 0 };
      acc.visits += Number(d.TOTAL_VISITS || d.total_visits || d.totalVisits || 0);
      acc.events += Number(d.TOTAL_EVENTS || d.total_events || d.totalEvents || 0);
      map.set(key, acc);
    });
    return Array.from(map.values()).sort((a, b) => a.day.localeCompare(b.day));
  }, [daily, fullRangeDays]);

  // Compute tool names from usersByTool
  const toolNames = useMemo(() => {
    const names = new Set();
    usersByTool.forEach(r => {
      const t = r.tool || r.TOOL;
      if (t) names.add(t);
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [usersByTool]);

  // Pivot usersByTool into table rows by username with columns per tool (visits)
  const usersTableColumns = useMemo(() => {
    const cols = [{ key: 'username', label: 'Nom' }];
    toolNames.forEach(t => cols.push({ key: t, label: t }));
    cols.push({ key: 'total', label: 'Total visites' });
    return cols;
  }, [toolNames]);

  const usersTableData = useMemo(() => {
    const byUser = new Map();
    usersByTool.forEach(r => {
      const username = r.username || r.USERNAME;
      const tool = r.tool || r.TOOL;
      const visits = Number(r.total_visits || r.TOTAL_VISITS || r.totalVisits || 0);
      const events = Number(r.total_events || r.TOTAL_EVENTS || r.totalEvents || 0);
      if (!username || !tool) return;
      const row = byUser.get(username) || { username, total: 0, eventsTotal: 0 };
      row[tool] = (row[tool] || 0) + visits;
      row.total += visits;
      row.eventsTotal += events;
      byUser.set(username, row);
    });
    return Array.from(byUser.values()).sort((a, b) => (b.total || 0) - (a.total || 0));
  }, [usersByTool]);

  const totals = useMemo(() => {
    const visits = chartData.reduce((acc, d) => acc + (Number(d.visits) || 0), 0);
    const events = chartData.reduce((acc, d) => acc + (Number(d.events) || 0), 0);
    const activeUsers = usersTableData.length;
    return { visits, events, activeUsers };
  }, [chartData, usersTableData]);

  return (
    <div className="space-y-4">
      {!clientFilter?.value && (
        <StatisticsClientsOverview
          startDate={startDate}
          endDate={endDate}
          toolFilter={toolFilter}
          fetchWithAuth={fetchWithAuth}
          apiBaseUrl={apiBaseUrl}
          includeInternal={includeInternal}
        />
      )}

      {clientFilter?.value && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatTile label="Visites (période)" value={totals.visits.toLocaleString('fr-FR')} className="text-2xl font-bold text-blue-800 dark:text-blue-200" tileClassName="bg-blue-50 dark:bg-blue-900/20" />
            <StatTile label="Événements (période)" value={totals.events.toLocaleString('fr-FR')} className="text-2xl font-bold text-green-800 dark:text-green-200" tileClassName="bg-green-50 dark:bg-green-900/20" />
            <StatTile label="Utilisateurs actifs" value={totals.activeUsers} className="text-2xl font-bold text-purple-800 dark:text-purple-200" tileClassName="bg-purple-50 dark:bg-purple-900/20" />
          </div>

          <div className="p-4 rounded-lg border dark:border-gray-700 h-72">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold dark:text-white">Évolution d'usage — {clientFilter.label || clientFilter.value}</h3>
              <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                <button
                  onClick={() => setMetric('visits')}
                  className={`px-3 py-1 text-xs font-medium rounded-md ${metric === 'visits' ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-300'}`}
                >
                  Visites
                </button>
                <button
                  onClick={() => setMetric('events')}
                  className={`ml-1 px-3 py-1 text-xs font-medium rounded-md ${metric === 'events' ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-300'}`}
                >
                  Événements
                </button>
              </div>
            </div>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey={metric} fill={metric === 'visits' ? '#3B82F6' : '#10B981'} name={metric === 'visits' ? 'Visites' : 'Événements'} />
              </BarChart>
            </ResponsiveContainer>
            {loadingDaily && <div className="text-xs text-gray-500 mt-2">Chargement...</div>}
          </div>

          <div className="p-4 rounded-lg border dark:border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold dark:text-white">Utilisateurs du client par outil</h3>
            </div>
            <SortableTable
              data={usersTableData}
              columns={usersTableColumns}
              emptyMessage={loadingUsers ? 'Chargement...' : 'Aucune donnée'}
              countLabel={`Utilisateurs: ${usersTableData.length}`}
              exportable={true}
              exportFilename={`utilisateurs_${clientFilter?.value}_${formatYMD(startDate)}_${formatYMD(endDate)}.csv`}
              withSum={true}
              disableFilters
              initialSortBy={{ key: 'total', direction: 'descending' }}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default StatisticsClientsTab;


